import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  AlarmNotificationProfilePrivileges,
  AlarmNotificationProfile,
  AdaptedAlarmNotificationProfile,
} from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import * as actions from 'data/alarmNotificationProfile/alarmNotificationProfile.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/alarmNotificationProfile/alarmNotificationProfile.selectors';
import cloneDeep from 'lodash.clonedeep';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import { AppToaster } from 'shared/toaster';
import AlarmNotificationProfileGrid from './components/AlarmNotificationProfileGrid';
import { alarmNotificationProfileColumnDefs } from '../../AlarmNotificationProfile.types';
import {
  adaptTemplateVisibilityMode,
  adaptAlarmNotificationProfileData
} from '../../utils/alarmNotificationProfileTemplate.adapter';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  data: selectors.getAlarmNotificationProfileList(state),
  selectedAlarmNotificationProfile: selectors.getSelectedAlarmNotificationProfile(state),
  deliveryFunctionId: state.global.selectedDF,
});

const mapDispatch = {
  goToAlarmNotificationProfileCreate: actions.goToAlarmNotificationProfileCreate,
  goToAlarmNotificationProfileEdit: actions.goToAlarmNotificationProfileEdit,
  goToAlarmNotificationProfileView: actions.goToAlarmNotificationProfileView,
  selectAlarmNotificationProfile: actions.selectAlarmNotificationProfile,
  getAlarmNotificationProfileList: actions.getAlarmNotificationProfileList,
  deleteAlarmNotificationProfile: actions.deleteAlarmNotificationProfile,
  getAlarmsDefList: actions.getAlarmsDefList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const AlarmNotificationProfileList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  data,
  selectedAlarmNotificationProfile,
  goToAlarmNotificationProfileCreate,
  goToAlarmNotificationProfileEdit,
  goToAlarmNotificationProfileView,
  selectAlarmNotificationProfile,
  getAlarmNotificationProfileList,
  deleteAlarmNotificationProfile,
  getAlarmsDefList
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(alarmNotificationProfileColumnDefs);
  const [gridData, setGridData] = useState<AdaptedAlarmNotificationProfile[]>([]);

  useEffect(() => {
    getAlarmNotificationProfileList();
    getAlarmsDefList()
  }, [getAlarmNotificationProfileList, getAlarmsDefList]);

  function addIdToGrid(gridData?: AdaptedAlarmNotificationProfile[]) {
    let clonedGridData = cloneDeep(gridData) || [];

    clonedGridData = clonedGridData?.map((row) => {
      return { ...row, id: row.alarmNtfyProfileId }
    });

    return clonedGridData;
  }

  useEffect(() => {
    setLastUpdatedAt(new Date());

    if (data) {
      const adaptedAlarmNotificationProfileGridData: AdaptedAlarmNotificationProfile[] = data
        .map((alarmNotificationProfile: AlarmNotificationProfile) => {
          return adaptAlarmNotificationProfileData(alarmNotificationProfile);
        });
      setGridData(addIdToGrid(adaptedAlarmNotificationProfileGridData));
    }

    return () => {
      AppToaster.clear();
    };
  }, [data]);

  const handleRefresh = () => getAlarmNotificationProfileList();

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToAlarmNotificationProfileCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectAlarmNotificationProfile(event.api.getSelectedRows());
  };

  const handleDeleteAlarmNotificationProfile = () => {
    const alarmNotificationProfileId = selectedAlarmNotificationProfile[0]?.alarmNtfyProfileId;
    if (alarmNotificationProfileId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedAlarmNotificationProfile[0]?.name,
        AuditService.ALARM_NOTIFICATION_PROFILE,
        AuditActionType.ALARM_NOTIFICATION_PROFILE_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteAlarmNotificationProfile(String(alarmNotificationProfileId), body);
  };

  const handleGoToEdit = () => goToAlarmNotificationProfileEdit(history);

  const handleGoToView = () => goToAlarmNotificationProfileView(history);

  const hasPrivilegeToView = privileges.includes(AlarmNotificationProfilePrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(AlarmNotificationProfilePrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(AlarmNotificationProfilePrivileges.Delete);

  return (
    <Fragment>
      <AlarmNotificationProfileGrid
        data={gridData}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedAlarmNotificationProfile={selectedAlarmNotificationProfile[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteAlarmNotificationProfile}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(AlarmNotificationProfileList));
